/**
 * Proposed Trades — pane 2 of the Trade Hub.
 *
 * What is in flight. Trade Match leads: it is the single most-suitable swap
 * pulled out of the trade list on the Potential pane, and it carries the Share
 * button that turns an earmarked pair into a real proposal — so it is the
 * bridge between pane 1 and this one, and belongs at the top.
 *
 * Under it, the live proposals — and ONLY the live ones. Agreeing now completes
 * a trade outright, so a row here is either pending (waiting on the other
 * trainer) or one of the legacy 'agreed' rows from before that change, which
 * still need their old two-sided finish.
 *
 * A completed trade must NEVER appear here. It did, on device: the pane showed
 * a row reading "Trade complete" while the same trade also sat in Completed.
 * The list is filtered by status in useTradeHubData rather than trusted from
 * the server, because the `?status=` scoping is newer than the deployed worker
 * and an older one returns every status mixed together.
 */
import type { ReactNode } from "react";
import { Send } from "lucide-react";

import { TradeMatchCard } from "@/components/TradeMatchCard";
import { TradeProposalsCard } from "@/components/TradeProposalsCard";
import { TradePageHero } from "@/components/trade/TradePageHero";
import type { TradePaneKey } from "@/components/trade/TradeHubTabs";
import type { TradeHubData } from "@/pages/trades/useTradeHubData";

export function ProposedTradesPane({
  hub,
  tabs,
  goToPane,
}: {
  hub: TradeHubData;
  tabs: ReactNode;
  goToPane: (key: TradePaneKey) => void;
}) {
  const {
    friendName, myAccountId,
    tradeHighlight, tradeMatchAllProposed, liveProposals, liveProposalsQ, proposalBusyId,
    proposalActionMut, completeProposalMut,
    proposeTrade, exportTradeCard,
  } = hub;

  const waitingOnThem = liveProposals.filter((p) => p.status === "pending").length;
  const agreed = liveProposals.filter((p) => p.status === "agreed").length;

  return (
    <>
      <TradePageHero
        eyebrow={`Trading with ${friendName}`}
        title="Proposed Trades."
        greenSuffix="In flight."
        subtitle={`Swaps you and ${friendName} have put to each other. Agreeing seals it. It moves straight to your history.`}
      >
        {tabs}
      </TradePageHero>

      {/* Trade Match — the single most-suitable swap, built from your trade list
          (falling back to the first surfaced lead pair). Asking moves it into
          the proposals list below and the card rolls on to the next pairing, so
          the card only ever shows a swap you have NOT put yet. */}
      <TradeMatchCard
        friendName={friendName}
        highlight={tradeHighlight}
        allProposed={tradeMatchAllProposed}
        onPropose={() => proposeTrade(tradeHighlight)}
        onExport={() => void exportTradeCard(tradeHighlight)}
      />

      {liveProposals.length > 0 && (
        <p
          className="mt-4 text-[11.5px]"
          style={{ color: "var(--text-tertiary)" }}
          data-testid="proposed-summary"
        >
          {waitingOnThem > 0 && `${waitingOnThem} waiting on a reply`}
          {waitingOnThem > 0 && agreed > 0 && " · "}
          {agreed > 0 && `${agreed} agreed before the rule changed, mark it done to file it`}
        </p>
      )}

      {/* TradeProposalsCard renders nothing when the list is empty, so the empty
          state is ours to state — and it has to point at where a proposal comes
          from, which is the pane next door. */}
      {liveProposals.length === 0 ? (
        <section className="v2-neon-card-soft mt-4 p-6 text-center" data-testid="proposed-empty">
          <div
            className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center"
            style={{ background: "var(--surface-dark-10)", border: "1.5px solid var(--accent-fill-glow)" }}
          >
            <Send className="w-5 h-5" style={{ color: "var(--accent-fill)" }} />
          </div>
          <h2 className="v2-display text-sm mb-1.5" style={{ color: "var(--text-ink-bright)" }}>
            {liveProposalsQ.isLoading ? "Loading proposals…" : "Nothing proposed yet"}
          </h2>
          {!liveProposalsQ.isLoading && (
            <>
              <p className="text-xs mb-4 leading-relaxed" style={{ color: "var(--text-ink-faint)" }}>
                Share the trade above, or earmark a give and a get on your trade list. A proposal
lands here until they agree, which files it straight into your history.
              </p>
              <button
                onClick={() => goToPane("potential")}
                data-testid="button-proposed-open-potential"
                className="v2-neon-cta w-full max-w-[260px] mx-auto py-3 text-sm"
              >
                Build your trade list
              </button>
            </>
          )}
        </section>
      ) : (
        <TradeProposalsCard
          proposals={liveProposals}
          myAccountId={myAccountId}
          friendName={friendName}
          busyId={proposalBusyId}
          onAgree={(id) => proposalActionMut.mutate({ id, action: "agree" })}
          onDecline={(id) => proposalActionMut.mutate({ id, action: "decline" })}
          onCancel={(id) => proposalActionMut.mutate({ id, action: "cancel" })}
          onComplete={(p) => completeProposalMut.mutate(p)}
          // The same export the Trade Match card runs, on a swap that has
          // already been asked for in app — the pairing is all it needs, and
          // the row hands it over already oriented to this trainer.
          onShareCard={(pairing) => void exportTradeCard(pairing)}
        />
      )}

      {/* Clears the tab bar's protruding centre coin. */}
      <div className="mb-8" aria-hidden />
    </>
  );
}
